/** Overshot bucket wheel. The standalone wheel layer uses the same frame and axle as the complete prop. */
import { checkEnvelope, checkScope, el, n, partGroup } from '../../svg';
import type { PieceRecipe, ResolvedPalette } from '../../contract';
import { amount, beam, grainRng, ground, line, ring, rivet, type WheelArt } from './machinerySvg';
export interface OvershotParams { readonly spokes: number; readonly wear: number; readonly moss: number }
export type OvershotRecipe = PieceRecipe<'mill.waterwheel_overshot.v1', OvershotParams>;
export const OVERSHOT_FRAME = { w: 190, h: 210 } as const;
export const MILL_OVERSHOT: OvershotRecipe = {
  schema: 'hush.asset-recipe.v1', id: 'mill.waterwheel_overshot.default.v1', generatorId: 'mill.waterwheel_overshot.v1',
  seed: 20071001, paletteId: 'millrace_amber', params: { spokes: 8, wear: 0.35, moss: 0.45 },
};
export function buildOvershot(recipe: OvershotRecipe, p: ResolvedPalette, scope: string): WheelArt {
  checkEnvelope(recipe, MILL_OVERSHOT.generatorId); checkScope(scope);
  const spokes = amount(recipe.params.spokes, 'spokes', 6, 10, true);
  const wear = amount(recipe.params.wear, 'wear'), moss = amount(recipe.params.moss, 'moss');
  const random = grainRng(recipe), parts: string[] = [], axle = { x: 95, y: 96 };
  const support = partGroup(parts, 'wheel-footings', ground(190, 210, p)
    + el('path', { d: 'M33 207L38 190L68 188L74 207ZM117 207L122 190L153 192L159 207Z', fill: p.stoneBase, stroke: p.outline, 'stroke-width': 1.5 })
    + beam(49, 190, 92, 99, 13, p) + beam(142, 190, 98, 99, 13, p)
    + beam(51, 179, 142, 179, 9, p) + rivet(61, 179, p) + rivet(132, 179, p));
  let spokeArt = '';
  for (let i = 0; i < spokes; i++) {
    const a = i * Math.PI * 2 / spokes;
    spokeArt += beam(Math.cos(a) * 11, Math.sin(a) * 11, Math.cos(a) * 70, Math.sin(a) * 70, 8, p);
  }
  let buckets = '';
  for (let i = 0; i < 20; i++) {
    const a = i * 18;
    let wood = el('path', { d: 'M-11 -85L10 -85L13 -69L-10 -69Z', fill: p.woodShadow, stroke: p.outline, 'stroke-width': 1.2 })
      + el('path', { d: 'M-11 -85L10 -85L9 -80L-10 -79Z', fill: p.woodLight })
      + line(-8, -74, 9, -75, p.woodBase, 1.5);
    wood += line(-8 + random(`scratch.${i}`) * 5, -82, 6, -81, p.woodShadow, 0.7 + wear);
    if (random(`moss.${i}`) < moss) wood += el('path', { d: 'M-9 -80Q-4 -85 2 -81L7 -80L2 -77L-7 -78Z', fill: p.moss, opacity: 0.8 });
    buckets += el('g', { transform: `rotate(${a})` }, wood);
  }
  const wheel = partGroup(parts, 'overshot-wheel', el('g', { id: `${scope}_wheel`, transform: `translate(${axle.x} ${axle.y})` },
    spokeArt + buckets + ring(0, 0, 72, 7, p)
    + el('circle', { cx: 0, cy: 0, r: 14, fill: p.paperShadow, stroke: p.outline, 'stroke-width': 2 })
    + el('circle', { cx: 0, cy: 0, r: 8, fill: p.paperBase, stroke: p.stoneShadow, 'stroke-width': 2 })
    + line(-4, -2, 5, 2, p.paperLight, 2)
    + el('path', { d: `M-65 -25L${n(-60 + random('rim-wear') * 4)} -36`, stroke: p.paperLight, 'stroke-width': 1, opacity: wear, fill: 'none' })));
  return { frame: OVERSHOT_FRAME, body: support + wheel, anchors: { support: { x: 95, y: 210 }, axle }, parts, openings: [], layers: { support, wheel } };
}
